import { useState, useEffect } from 'react';
import { getAuthHeaders } from '../../services/apiClient';
import { AlertCircle, Copy, RefreshCw, CheckCircle2, Loader } from 'lucide-react';

interface AdaptScriptResponse {
  status: string;
  adapted_script: string;
  rollback_script?: string;
  changes_made: string[];
  confidence: string;
  explanation: string;
  tokens_used: number;
  cost_estimated: number;
}

interface TableSchema {
  name: string;
  description: string;
  fields: Array<{ name: string; field_type: string; description: string; nullable: boolean; constraints: string[]; examples: string[] }>;
}

interface AdaptedScriptViewerProps {
  issueId: number;
  templateScript: string;
  scriptId: number;
  problemSummary: string;
  rootCause: string;
  impactDescription: string;
  extractedEntities: Array<{ entity_type: string; value: string; confidence: number }>;
  criticalFields: string[];
  supportingFields: string[];
  relevantTables: Array<{ name: string; description: string; fields: Array<{ name: string; field_type: string; description: string; nullable: boolean; constraints: string[]; examples: string[] }> }>;
  moduleCategory: string;
  detectedModules?: string[];
  onClose: () => void;
}

export function AdaptedScriptViewer({
  issueId,
  templateScript,
  scriptId,
  problemSummary,
  rootCause,
  impactDescription,
  extractedEntities,
  criticalFields,
  supportingFields,
  relevantTables,
  moduleCategory,
  detectedModules = [],
  onClose,
}: AdaptedScriptViewerProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [result, setResult] = useState<AdaptScriptResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [tables, setTables] = useState<TableSchema[]>(relevantTables);

  // Fetch table schemas from the existing endpoint when component mounts or when dependencies change
  useEffect(() => {
    const fetchTables = async () => {
      if (!detectedModules || detectedModules.length === 0) {
        return;
      }

      try {
        const response = await fetch('/api/v1/issue-analysis/tables', {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({
            modules: detectedModules,
            script_content: templateScript,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.tables) {
            setTables(data.tables);
          }
        }
      } catch (err) {
        console.warn('Failed to fetch table schemas:', err);
        // Fallback to the provided relevantTables
        setTables(relevantTables);
      }
    };

    fetchTables();
  }, [detectedModules, templateScript, relevantTables]);

  const handleAdapt = async () => {
    setState('loading');
    setError('');

    try {
      const response = await fetch(
        `/api/v1/issue-analysis/${issueId}/adapt-script`,
        {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({
            script_id: scriptId,
            template_script: templateScript,
            problem_summary: problemSummary,
            root_cause: rootCause,
            impact_description: impactDescription,
            extracted_entities: extractedEntities,
            critical_fields: criticalFields,
            supporting_fields: supportingFields,
            relevant_tables: tables,
            module_category: moduleCategory,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to adapt script');
      }

      const data = await response.json();
      setResult(data);
      setState('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setState('error');
    }
  };

  const handleCopyAdapted = () => {
    if (result) {
      navigator.clipboard.writeText(result.adapted_script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (state === 'idle') {
    return (
      <button
        onClick={handleAdapt}
        className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 px-3 py-2 rounded bg-blue-50 hover:bg-blue-100 transition-colors"
      >
        <RefreshCw size={12} />
        Adapt for This Issue
      </button>
    );
  }

  if (state === 'loading') {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <Loader size={14} className="animate-spin text-blue-600" />
        <span className="text-xs text-gray-600">Adapting with Claude...</span>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-red-700">Adaptation failed</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={() => setState('idle')}
          className="text-xs text-blue-600 hover:text-blue-800 font-semibold mt-2"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  return (
    <div className="border border-blue-200 rounded-lg bg-blue-50/30 p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={14} className="text-green-600" />
          <span className="text-xs font-semibold text-gray-800">Adapted Successfully</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded ${
              result.confidence === 'high'
                ? 'bg-green-100 text-green-700'
                : result.confidence === 'medium'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {result.confidence === 'high' ? '✓' : '?'} {result.confidence.charAt(0).toUpperCase() + result.confidence.slice(1)}{' '}
            Confidence
          </span>
        </div>
      </div>

      {/* Changes Summary */}
      <div>
        <p className="text-xs font-semibold text-gray-700 mb-1">Changes Made:</p>
        <ul className="text-xs text-gray-600 space-y-1 ml-4">
          {result.changes_made.map((change, i) => (
            <li key={i} className="list-disc">
              {change}
            </li>
          ))}
        </ul>
      </div>

      {/* Explanation */}
      <div>
        <p className="text-xs font-semibold text-gray-700 mb-1">Why:</p>
        <p className="text-xs text-gray-600">{result.explanation}</p>
      </div>

      {/* Script Comparison */}
      <div className="grid grid-cols-2 gap-2">
        {/* Original */}
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1">Original Template:</p>
          <div className="bg-gray-100 p-2 rounded border border-gray-200 max-h-32 overflow-y-auto">
            <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap break-words">
              {templateScript}
            </pre>
          </div>
        </div>

        {/* Adapted */}
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1">Adapted Script:</p>
          <div className="bg-blue-100 p-2 rounded border border-blue-300 max-h-32 overflow-y-auto">
            <pre className="text-xs font-mono text-blue-900 whitespace-pre-wrap break-words">
              {result.adapted_script}
            </pre>
          </div>
        </div>
      </div>

      {/* Copy Button */}
      <button
        onClick={handleCopyAdapted}
        className="w-full text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold px-3 py-2 rounded flex items-center justify-center gap-1 transition-colors"
      >
        <Copy size={11} />
        {copied ? 'Copied!' : 'Copy Adapted Script'}
      </button>

      {/* Rollback Script */}
      {result.rollback_script && (
        <details className="bg-white border border-gray-200 rounded p-2">
          <summary className="text-xs font-semibold text-gray-700 cursor-pointer hover:text-gray-900">
            Show Rollback Script
          </summary>
          <div className="mt-2 bg-gray-50 p-2 rounded border border-gray-200 max-h-24 overflow-y-auto">
            <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap break-words">
              {result.rollback_script}
            </pre>
          </div>
        </details>
      )}

      {/* Metadata */}
      <div className="text-xs text-gray-500 border-t border-blue-200 pt-2">
        <span>Tokens used: {result.tokens_used} (~${result.cost_estimated.toFixed(4)})</span>
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        className="text-xs text-gray-600 hover:text-gray-800 font-semibold"
      >
        ← Back to Scripts
      </button>
    </div>
  );
}
